import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { getRequest, postRequest, deleteRequest, tabulatorGet } from '../../api/ApiController';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  mergeMaintenanceServiceTaskPagedListFiltersAndSorters,
  safeParse,
} from '../../utils/tabulatorProgressiveLoading';
import {
  applyServiceTaskColumnLayout,
  restoreServiceTaskUserMaskToTable,
} from '../../utils/tabulatorServiceTaskLayout';
import { getEffectiveMask } from '../../utils/effectiveMask';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createSaveUserMaskColumns,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../utils/maintenanceTabulatorMaskActions';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { TABULATOR_LIST_ID_MIN_WIDTH } from '../../utils/tabulatorColumnMinWidth';
import {
  TABULATOR_AUDIT_PROFILES,
  buildAuditTabulatorColumns,
  mergeColumnVisibilityWithAuditDefaults,
} from '../../utils/tabulatorAuditColumns';
import { SaveIcon, AddIcon, CopyIcon, PrintIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon, LoadPrioIcon, InfoIcon, ErrorIcon, TaskIcon, PermissionIcon, NeedIcon, EmergencyIcon,EditRowIcon, DeleteIcon, OutlookMsgIcon, BorrowItemIcon, UnseenIcon, SeenIcon, TechChangeIcon,EditIcon,CollapseTreeIcon,ExpandTreeIcon,BranchTreeIcon} from "../svg/svgIcons";
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import AuthContext from '../../components/AuthContext';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { ReactTabulator,reactFormatter } from 'react-tabulator';
import { DateTime } from "luxon";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css"
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import UtilContext from '../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../components/maintenance/MaintenanceMaskDialogCheckbox';
import { PanelMenu } from 'primereact/panelmenu';
import { Checkbox } from 'primereact/checkbox';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import ReactDOMServer from 'react-dom/server';
import { Avatar } from 'primereact/avatar';
import { AvatarGroup } from "primereact/avatargroup";
import { Tag } from 'primereact/tag';
import { MultiSelect } from "primereact/multiselect";
import { createRoot } from "react-dom/client";
import { createAsyncNavigateHandlers } from '../../utils/asyncArrayNavigate';
import { navigationContextToQueryParams } from '../../utils/maintenanceNavigationContext';
import StatusIndicator, { MachineStateDot, getStatusIndicatorFilterValue } from '../../components/StatusIndicator';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { getPusherChannel } from '../../utils/pusherChannel';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../utils/printChrome';
import { getTimezone } from '../../utils/moduleSettings';
import { LocaleContext } from '../../components/LocaleContext';

const MASK_PAGE = 'MaintenanceTaskRecordDetail';

const TASK_RECORD_DETAIL_COLUMN_FIELD_ORDER = Object.freeze([
  'editor',
  'status_indicator',
  'contractor_id',
  'id',
  'type',
  'is_unread',
  'defined_id',
  'theme',
  'machine.name',
  'status_id',
  'waiting_for_action_by',
  'created_at',
  'updated_at',
]);

const DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY = Object.freeze({
  editor: true,
  status_indicator: true,
  id: true,
  type: true,
  is_unread: true,
  defined_id: true,
  theme: true,
  'machine.name': true,
  status_id: true,
  waiting_for_action_by: true,
  created_at: true,
  updated_at: true,
});

function mergeTaskRecordDetailColumnVisibilityFromSaved(saved) {
  return mergeColumnVisibilityWithAuditDefaults(DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY, saved);
}

function applyTaskRecordDetailDataColumnVisibility(table, visibility) {
  if (!table) return;
  table.hideColumn?.('contractor_id');
  for (const field of Object.keys(DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY)) {
    if (visibility[field] === false) table.hideColumn?.(field);
    else table.showColumn?.(field);
  }
}

export default function MaintenanceTaskRecordDetail({machine, type, navigationContext}){

    const [state,dispatch] = useContext(DatabaseContext);
    const {user} = useContext(AuthContext);
    const {setDialogText,setVisible,openFavoriteDialog,toast,setFastView,setUserMaskFilter,setStandardMaskFilter,getUserColor,getUserInitials,getDateFormat, openChosenNotificationPreferences} = useContext(UtilContext);
    const canSetStandardMask = user?.permissions?.includes('set standard mask');
    const { t } = useContext(LocaleContext);

    const ref = useRef(null);
    const overlayRef = useRef(null);
    const inFlightRef = useRef({ sig: null, promise: null });
    const completedCacheRef = useRef(new Map());
    const tableBuiltRef = useRef(false);
    const isRestoringRef = useRef(false);
    const savedHScrollRef = useRef({ left: 0, pending: false });

    const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
    const refreshFromStart = useCallback(() => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingData')), [getTable]);

    const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY }));
    const [companyContactsList, setCompanyContactsList] = useState([]);
    const [companyContacts, setCompanyContacts] = useState([]);
    const [machines, setMachines] = useState(state.machines);
    const [selectedRecord,setSelectedRecord] = useState(null);
    const effectiveCompanyContacts = (state.company_contacts || []).length ? (state.company_contacts || []) : companyContactsList;

    useEffect(() => {
        if ((state.company_contacts || []).length > 0) return;
        getRequest("/api/company_contacts?minimal=1&module=maintenance")
            .then((r) => { const list = r?.data ?? r ?? []; setCompanyContactsList(Array.isArray(list) ? list : []); })
            .catch(() => {});
    }, [state.company_contacts?.length]);

    const [maskVisible, setMaskVisible] = useState(false);
    const [formMachine,setFormMachine] = useState(machine);
    const currentMachineIdRef = useRef(machine?.id ?? null);
    const prevMachineIdRef = useRef(machine?.id);

    useEffect(() => {
        if (machine?.id != null) setFormMachine(machine);
    }, [machine?.id]);

    useEffect(() => {
        currentMachineIdRef.current = formMachine?.id ?? null;
    }, [formMachine?.id]);

    const pageSizePc = useMemo(() => {
        const v = (state.module_settings || []).find(s => s.module === 'global' && s.key === 'page_size_pc')?.value;
        const n = parseInt(v, 10);
        return (Number.isFinite(n) && n > 0) ? n : 50;
    }, [state.module_settings]);

    const buildRequestParams = useCallback((paramsFromTabulator = {}) => {
        const extra = { ...extractSafePaginationParams(paramsFromTabulator), filter_type: 'Error' };
        const machineId = currentMachineIdRef.current;
        if (machineId != null) extra.filter_machine_id = machineId;
        if (extra.size == null) extra.size = pageSizePc;
        mergeMaintenanceServiceTaskPagedListFiltersAndSorters(extra, {
            getTable,
            tableBuiltForParams: tableBuiltRef.current,
            userMaskFilters: state.user_mask_filters,
            maskPage: MASK_PAGE,
            service_states: state.service_states ?? [],
            userId: user?.id,
            module_settings: state?.module_settings,
            getDateFormat,
            definedIdMapsToTheme: false,
        });
        return extra;
    }, [state.service_states, state?.module_settings, state.user_mask_filters, user?.id, getDateFormat, pageSizePc, getTable]);

    const captureHScroll = useCallback(() => {
        const table = getTable();
        if (!table) return;
        const holder = table.rowManager?.element;
        if (!holder) return;
        savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
    }, [getTable]);

    const restoreHScroll = useCallback(() => {
        if (!savedHScrollRef.current.pending) return;
        const table = getTable();
        if (!table) return;
        const holder = table.rowManager?.element;
        const header = table.columnManager?.contentsElement;
        if (!holder) return;
        const left = savedHScrollRef.current.left;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                holder.scrollLeft = left;
                if (header) header.scrollLeft = left;
                savedHScrollRef.current.pending = false;
            });
        });
    }, [getTable]);

    useEffect(() => {
        const channel = getPusherChannel();
        if (!channel) return;
        const onUpdated = () => { refreshFromStart(); };
        const onDeleted = () => { refreshFromStart(); };
        channel.bind('service_task.updated', onUpdated);
        channel.bind('service_task.deleted', onDeleted);
        return () => {
            channel.unbind('service_task.updated', onUpdated);
            channel.unbind('service_task.deleted', onDeleted);
        };
    }, [refreshFromStart]);

    //TODO dorobit contractor id
    const [scrollIcons,setScrollIcons] = useState((user.permissions?.includes("edit maintenance"))?false:true);
    const [editIcon,setEditIcon] = useState(true);
    const [deleteIcon,setDeleteIcon] = useState(true);

    const [visibleRecord,setVisibleRecord] = useState(false);
    const [images,setImages] = useState('');
    const [updatefileList, setUpdateFileList] = useState([]);
    const updateFileListRef = useRef(null);

    const onSelectRow = (data, rows) => {
        setEditIcon(true);
        setDeleteIcon(true);
        if(ref.current.getSelectedData().length>0){
        if(ref.current.getSelectedData().length == 1){
            if(user.permissions?.includes("edit maintenance"))setEditIcon(false);
        }
        if(user.permissions?.includes("delete maintenance"))setDeleteIcon(false);
        }
    };

    const validate = values => {
        const errors = {};
        /*if (!values.type) {
            errors.type = t('Common.RequiredField');
        }*/
        return errors;
    };

    function changeCompany(company_id){
        setCompanyContacts(effectiveCompanyContacts.filter(contact => contact.company_id == company_id));
        formikUpdate.setFieldValue('company_id',company_id);
        formikUpdate.setFieldValue('company_contact_id','');
        formikUpdate.setFieldValue("machines",[]);
    }

    function getRecordFiles(id){
        getRequest(`/api/maintenance_scheduled_records/protocols/${id}`).then((response) => {
            console.log(response);
            if(response.data!=="{}")setImages(response.data);
        });
    }

    useEffect(() => {
            // Update the document title using the browser API
        if(selectedRecord!=null){
            console.log(selectedRecord);
            console.log(formikUpdate.values);
            //getRecordFiles(selectedRecord.id);
        }
    },[selectedRecord]);

    const formikUpdate = useFormik({
        initialValues: selectedRecord ? 
            {id: selectedRecord?.id, maintenance_type_id: selectedRecord?.maintenance_type_id,
            last_execution_date:selectedRecord?.last_execution_date,description:selectedRecord?.description,
            company_id:selectedRecord?.company_id,company_contact_id:selectedRecord?.company_contact_id} : 
            {maintenance_type_id: '',last_execution_date: '',description:'',company_id:'',company_contact_id:''},
        enableReinitialize:true,
        validate,
        onSubmit: values => {
            let formData = new FormData();
            for (let value in values) {
                formData.append(value, values[value] != null ? values[value] : "");
            }
            for(let i = 0;i<updatefileList.length;i++){
                formData.append('files[]',updatefileList[i]);
            }
            postRequest(`/api/maintenance_scheduled_records/update/${formikUpdate.values.id}`, formData,true)
            .then((response) => {
                setVisibleRecord(false);
                formikUpdate.resetForm();
                if (updatefileList.length) {
                    setUpdateFileList([]);
                    if (updateFileListRef.current) updateFileListRef.current.value = null;
                }
                toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordSavedDetail') });
                if (formMachine?.id) {
                    getRequest(`/api/machine_cards/by_machine/${formMachine.id}`).then((r) => {
                        const d = r?.data?.data ?? r?.data;
                        if (d) setFormMachine(d);
                    }).catch(() => {});
                }
                getTable()?.replaceData?.();
            }).catch((reason) => {
                if(!navigator.online){
                    alert(t('ServiceTask.OfflineQueuedAlert'));
                }
                else{
                    alert(t('Common.Toast.ActionFailedDetail'));
                }
            });
        },
    });

    function hideEditRecord(){
        formikUpdate.resetForm();
        setVisibleRecord(false);
    }

    const resetDetailUiOnNavigate = useCallback(() => {
        setVisibleRecord(false);
        setSelectedRecord(null);
        setImages('');
        setUpdateFileList([]);
        if (updateFileListRef.current) updateFileListRef.current.value = null;
        setEditIcon(true);
        setDeleteIcon(true);
        setMaskVisible(false);
        formikUpdate.resetForm();
    }, [formikUpdate]);

    useEffect(() => {
        const prevId = prevMachineIdRef.current;
        const nextId = formMachine?.id;
        if (prevId === nextId) return;
        prevMachineIdRef.current = nextId;
        if (nextId == null || !tableBuiltRef.current) return;
        inFlightRef.current = { sig: null, promise: null };
        completedCacheRef.current.clear();
        captureHScroll();
        refreshFromStart();
    }, [formMachine?.id, captureHScroll, refreshFromStart]);

    const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useMemo(() => {
        const applyMachine = (data) => {
            if (!data) return;
            resetDetailUiOnNavigate();
            currentMachineIdRef.current = data.id ?? null;
            setFormMachine(data);
        };
        const buildFetchRecord = (toParams = () => '') => async (direction, currentId) => {
            setDialogText?.(t('Common.Loading'));
            setVisible?.(true);
            try {
                const q = toParams();
                const url = `/api/machines/navigate?direction=${direction}${currentId ? '&id=' + currentId : ''}${q ? '&' + q : ''}`;
                const resp = await getRequest(url);
                return resp?.data ?? null;
            } finally {
                setVisible?.(false);
            }
        };
        if (navigationContext?.filters != null || navigationContext?.sorters != null || navigationContext?.extra != null) {
            const toParams = () => navigationContextToQueryParams(navigationContext);
            return createAsyncNavigateHandlers(buildFetchRecord(toParams), applyMachine, () => currentMachineIdRef.current ?? formMachine?.id);
        }
        return createAsyncNavigateHandlers(buildFetchRecord(), applyMachine, () => currentMachineIdRef.current ?? formMachine?.id);
    }, [navigationContext, formMachine?.id, resetDetailUiOnNavigate, setDialogText, setVisible]);

    const accept = () => {
        const rows_to_delete = getTable()?.getSelectedData?.() ?? [];
        rows_to_delete.forEach(element => {
            deleteRequest(`/api/maintenance_scheduled_records/${element.id}`)
            .then((response) => {
                toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordDeletedDetail') });
            }).catch((reason) => {
                alert(reason);
                toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.DeleteFailedDetail'), life: 3000 });
            });
        });
        if (formMachine?.id) {
            getRequest(`/api/machine_cards/by_machine/${formMachine.id}`).then((r) => {
                const d = r?.data?.data ?? r?.data;
                if (d) setFormMachine(d);
            }).catch(() => {});
        }
        getTable()?.replaceData?.();
    }
        
    const reject = () => {
        toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.RequestCancelled'), life: 3000 });
    }

    const confirm = () => {
        confirmDialog({
            message: t('Common.ConfirmDeleteRecord'),
            header: t('Common.ConfirmDelete'),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Common.Yes'),
            rejectLabel: t('Common.No'),
            accept,
            reject
        });
    };

    const acceptFile = (id) => {
        getRequest(`/api/maintenance_scheduled_records/deleteProtocol/${id}`)
        .then((response) => {
            toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.FileDeletedDetail') });
            getRecordFiles(selectedRecord?.id);
        }).catch((reason) => {
            alert(reason);
            toast?.current?.show({ severity: 'warn', summary: t('Common.Rejected'), detail: t('Common.Toast.FileDeleteFailedDetail'), life: 3000 });
        })
    };

    function deleteFiles(img){
        confirmDialog({
            message: t('Common.ConfirmDeleteFile'),
            header: t('Common.ConfirmDelete'),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Common.No'),
            rejectLabel: t('Common.Yes'),
            accept:reject,
            reject:() => acceptFile(img.id)
        });
    }

    const normalizeText = (value) => (value || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    const EditRowFormatter = (props) => {
        const rowData = props.cell.getRow().getData();

        const handleView = async () => {
            // Otvor detail tabu
            dispatch({
                type: 'create_new_tab',
                name: t('Util.Tab.ServiceTaskDetail'),
                translationKey: 'Util.Tab.ServiceTaskDetail',
                legacyLabel: t('Util.Tab.ServiceTaskDetail'),
                page: 'ServiceTaskDetail',
                props: { task: rowData, taskId: rowData?.id },
                mask: 'intr004'
            });
        };

        return (
            <div className="flex justify-center items-start w-full h-full">
                <button type="button" onClick={handleView}>
                    <EditRowIcon />
                </button>
            </div>
        );
    };

    function TypeFormatter(props) {
        const rowData = props.cell.getRow().getData();
        let icon;
        switch (rowData.type) {
            case 'Notification': icon = <InfoIcon width={6} height={6} />; break;
            case 'Error': icon = <ErrorIcon width={6} height={6} />; break;
            case 'Task': icon = <TaskIcon width={6} height={6} />; break;
            case 'Permission': icon = <PermissionIcon width={6} height={6} />; break;
            case 'Need': icon = <NeedIcon width={6} height={6} />; break;
            case 'Borrow': icon = <BorrowItemIcon width={6} height={6} />; break;
            case 'Emergency': icon = <EmergencyIcon width={6} height={6} />; break;
            case 'TechChange': icon = <TechChangeIcon width={8} height={8} />; break;
            default: icon = <InfoIcon width={6} height={6} />; break;
        }
        return <div className="flex items-center justify-center w-full h-full">{icon}</div>;
    }

    const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
    const dateTimeFmt = `${dateFmt} HH:mm`;
    const timezone = getTimezone(state?.module_settings || []);

    function StatusIndicatorFormatter(props) {
        const rowData = props.cell.getRow().getData();
        return (
            <div className="flex items-center justify-center w-full h-full">
                <StatusIndicator task={rowData} moduleSettings={state.module_settings} />
            </div>
        );
    }
    function MachineNameFormatter(props) {
        const rowData = props.cell.getRow().getData();
        const machineName = rowData?.machine?.name ?? rowData['machine.name'] ?? '—';
        return (
            <div className="flex items-center min-w-0 gap-2">
                <MachineStateDot task={rowData} />
                <span className="truncate" title={machineName}>{machineName}</span>
            </div>
        );
    }

    function StatusFormatter(props) {
        const rowData = props.cell.getRow().getData();

        // základný status podľa status_id
        let status = state.service_states.find(s => s.id === rowData.status_id);

        // fallback ak sa nenašiel status
        if (!status) {
            return (
                <Tag className="py-0.5 px-1 bg-gray-400">
                    <span className="text-base font-normal">{'Neznámy'}</span>
                </Tag>
            );
        }

        // 🔑 podmienka pre system_inspection
        if (status.phase === 'system_inspection') {
            const execStateId = rowData.execution?.executor_state;

            if (execStateId) {
                const execState = state.service_states.find(s => s.id == execStateId);
                if (execState && execState.phase === 'execution' && execState.transition_type === 'non_final') {
                    status = execState;
                }
            } else {
                // fallback podľa user_department
                let fallbackState = null;

                if (rowData.execution?.user_department === "user") {
                    fallbackState = state.service_states.find(s => s.id === 9); // Pridelený
                } else if (rowData.execution?.user_department === "department") {
                    fallbackState = state.service_states.find(s => s.id === 10); // Pridelený oddeleniu
                } else {
                    fallbackState = state.service_states.find(s => s.id === 7); // Schválený
                }

                if (fallbackState) {
                    status = fallbackState;
                }
            }
        }

        const hexColor = status.color ? `#${status.color}` : '#9ca3af';

        return (
            <Tag className="py-0.5 px-1" style={{ backgroundColor: hexColor }}>
                <span className="text-base font-normal">{status.name}</span>
            </Tag>
        );
    }

    function AwaitingUserFormatter(props) {
        const rowData = props.cell.getRow().getData();

        const STATUS = {
            UZAVRENY: 28,
            NESCHVALENY: 18,
            STORNOVANY: 30,
            // prípadne ďalšie
        };

        const deadline = rowData?.deadline
            ? DateTime.fromISO(rowData.deadline)
            : null;
        const today = DateTime.now().setZone(timezone).startOf("day");
        const isExpired = deadline && deadline < today;
        const isClosedStatus = Object.values(STATUS).includes(rowData?.status_id);

        const users = rowData.awaiting_user || [];
        const maxVisible = 3;

        const visibleUsers = users.slice(0, maxVisible);
        const extraUsers = users.slice(maxVisible);
        const extraCount = extraUsers.length;

        const extraNames = extraUsers
            .map((user) => user?.employee?.name || user?.username)
            .filter(Boolean);

        return (
            <div className="flex items-center justify-start w-full h-full min-w-0 flex-wrap gap-1">
                {!isClosedStatus && users.length > 0 && (
                    <AvatarGroup>
                        {visibleUsers.map((user) => (
                            <Avatar
                                key={user.id}
                                title={user?.employee?.name || user?.username}
                                label={getUserInitials(
                                    user?.employee?.name || user?.username
                                )}
                                shape="circle"
                                style={{
                                    backgroundColor: getUserColor(user?.username),
                                    color: "#ffffff",
                                    marginRight: "5px",
                                }}
                            />
                        ))}

                        {extraCount > 0 && (
                            <Avatar
                                label={`+${extraCount}`}
                                shape="circle"
                                // keď podržíš myš, ukáže mená tých "skrytých"
                                title={extraNames.join("\n")}
                                style={{
                                    backgroundColor: "#6b7280", // neutrálna sivá
                                    color: "#ffffff",
                                    marginRight: "5px",
                                }}
                            />
                        )}
                    </AvatarGroup>
                )}

                {deadline && (
                    <div
                        className={
                            isExpired && !isClosedStatus
                                ? "text-red-500 font-semibold shrink-0"
                                : "shrink-0"
                        }
                    >
                        {deadline.toFormat(dateFmt)}
                    </div>
                )}
            </div>
        );
    }

    // sorter funkcia
    function awaitingUserSorter(a, b, rowA, rowB) {
        const dataA = rowA.getData();
        const dataB = rowB.getData();

        const deadlineA = dataA?.deadline
            ? DateTime.fromISO(dataA.deadline)
            : null;
        const deadlineB = dataB?.deadline
            ? DateTime.fromISO(dataB.deadline)
            : null;

        if (deadlineA && deadlineB) {
            if (deadlineA < deadlineB) return -1;
            if (deadlineA > deadlineB) return 1;
            return 0;
        }

        if (deadlineA && !deadlineB) return -1; // A má dátum, B nie
        if (!deadlineA && deadlineB) return 1;  // B má dátum, A nie

        return 0; // obaja nemajú deadline
    }

    const markAsViewed = async (taskId, value) => {
        const formData = new FormData();
        formData.append("task_id", taskId);
        formData.append("user_id", user.id);
        const url = !value
            ? `/api/service_tasks/${taskId}/mark-as-viewed`
            : `/api/service_tasks/${taskId}/mark-as-not-viewed`;
        try {
            await postRequest(url, formData, true);
            dispatch({ type: "updateTaskViewed", payload: taskId, value: value });
        } catch (err) {
            // fallback, nech sa UI zosynchronizuje
            //dispatch({ type: "updateTaskViewed", payload: taskId, value: value });
        }
    };

    
    function MailIconTemplate(props) {
    const rowData = props.cell.getRow().getData();
    const isUnread = rowData?.is_unread;
    return (
    <div className="flex items-center justify-center w-full h-full gap-2">
        {isUnread ? (
        <button type="button" title={t('Common.MarkAsRead')} onClick={() => markAsViewed(rowData.id, false)} className="hover:opacity-75">
            <UnseenIcon />
        </button>
        ) : (
        <button type="button" title={t('Common.MarkAsUnread')} onClick={() => markAsViewed(rowData.id, true)} className="hover:opacity-75">
            <SeenIcon />
        </button>
        )}
    </div>
    );
    }

    const textHeaderFilter = useCallback((headerValue, rowValue) => {
        if (!headerValue) return true;
        return normalizeText(rowValue).includes(normalizeText(headerValue));
    }, []);

    const remoteHeaderFilter = useCallback(() => true, []);

    const serviceTaskAuditTailColumns = useMemo(() => {
        const cols = buildAuditTabulatorColumns({
            profile: TABULATOR_AUDIT_PROFILES.contractorLastTouched,
            includeId: false,
            dateFormat: dateFmt,
            dateTimeFormat: dateTimeFmt,
            getUserColor,
            getUserInitials,
            textHeaderFilter,
            remotePassthrough: true,
        });
        return cols.map((col) => {
            if (col.field === 'created_at') return { ...col, title: t('Common.TicketCreatedAt') };
            if (col.field === 'updated_at') return { ...col, title: t('Common.Column.UpdatedAt') };
            return col;
        });
    }, [dateFmt, dateTimeFmt, getUserColor, getUserInitials, textHeaderFilter]);

    const statusFilterOptions = useMemo(
        () => (state.service_states || []).map((s) => ({ label: s.name, value: Number(s.id) })).sort((a, b) => a.label.localeCompare(b.label, 'sk')),
        [state.service_states]
    );

    const columns = useMemo(
        () => [
            {
                title: '',
                field: 'editor',
                hozAlign: 'center',
                width: 44,
                minWidth: 44,
                headerSort: false,
                formatter: reactFormatter(<EditRowFormatter />),
                resizable: false,
                frozen: true,
                print: false,
                download: false,
            },
            {
                title: '',
                field: 'status_indicator',
                hozAlign: 'center',
                width: 44,
                minWidth: 44,
                headerSort: false,
                formatter: reactFormatter(<StatusIndicatorFormatter />),
                resizable: false,
                frozen: true,
                print: false,
                download: false,
                headerFilter: 'list',
                headerFilterParams: { values: { '': t('Common.FilterAll'), red: '🔴', orange: '🟠', green: '🟢' }, clearable: true },
                headerFilterFunc: (headerValue, rowValue, rowData) => {
                    if (!headerValue) return true;
                    return getStatusIndicatorFilterValue(rowData, state.module_settings) === headerValue;
                },
            },
            {
                field: 'contractor_id',
                visible: false,
                width: 0,
                minWidth: 0,
                maxWidth: 0,
                resizable: false,
                headerSort: false,
                print: false,
                download: false,
            },
            {
                title: 'ID',
                field: 'id',
                headerFilter: inputOnEnterHeaderFilter,
                headerFilterLiveFilter: false,
                headerFilterFunc: remoteHeaderFilter,
                minWidth: TABULATOR_LIST_ID_MIN_WIDTH,
                widthGrow: 1,
                sorter: 'number',
                hozAlign: 'left',
            },
            {
                title: t('Common.Type'),
                field: 'type',
                formatter: reactFormatter(<TypeFormatter />),
                minWidth: 120,
                headerFilter: primeMultiSelectHeaderFilter,
                headerFilterParams: {
                    placeholder: t('Common.All'),
                    valueType: 'string',
                    options: [
                        { label: t('Common.Notification'), value: 'Notification' },
                        { label: t('Common.Error'), value: 'Error' },
                        { label: t('Common.Task'), value: 'Task' },
                        { label: t('Common.TechnicalChange'), value: 'TechChange' },
                        { label: 'Povolenie', value: 'Permission' },
                        { label: 'Potreba', value: 'Need' },
                        { label: t('Common.Loan'), value: 'Borrow' },
                        { label: 'Nehoda', value: 'Emergency' },
                    ].sort((a, b) => a.label.localeCompare(b.label, 'sk')),
                },
                headerFilterFunc: remoteHeaderFilter,
            },
            {
                title: t('Common.Read'),
                field: 'is_unread',
                minWidth: 100,
                formatter: reactFormatter(<MailIconTemplate />),
                headerFilter: primeMultiSelectHeaderFilter,
                headerFilterParams: {
                    placeholder: t('Common.All'),
                    valueType: 'string',
                    options: [
                        { label: t('Common.Unread'), value: 'true' },
                        { label: t('Common.Read'), value: 'false' },
                    ],
                },
                headerFilterFunc: remoteHeaderFilter,
            },
            {
                title: t('Common.TicketNumber'),
                field: 'defined_id',
                minWidth: 150,
                headerFilter: inputOnEnterHeaderFilter,
                headerFilterLiveFilter: false,
                headerFilterFunc: remoteHeaderFilter,
            },
            {
                title: t('Common.Name'),
                field: 'theme',
                minWidth: 250,
                widthGrow: 2,
                headerFilter: inputOnEnterHeaderFilter,
                headerFilterLiveFilter: false,
                headerFilterFunc: remoteHeaderFilter,
            },
            {
                title: 'Stroj',
                field: 'machine.name',
                minWidth: 200,
                formatter: reactFormatter(<MachineNameFormatter />),
                headerFilter: inputOnEnterHeaderFilter,
                headerFilterLiveFilter: false,
                headerFilterFunc: remoteHeaderFilter,
            },
            {
                title: t('Common.Status'),
                field: 'status_id',
                minWidth: 200,
                formatter: reactFormatter(<StatusFormatter />),
                headerFilter: primeMultiSelectHeaderFilter,
                headerFilterParams: { placeholder: t('Common.All'), options: statusFilterOptions },
                headerFilterFunc: remoteHeaderFilter,
            },
            {
                title: 'Vykonanie/Deadline',
                field: 'waiting_for_action_by',
                minWidth: 220,
                formatter: reactFormatter(<AwaitingUserFormatter />),
                sorter: awaitingUserSorter,
                headerFilter: inputOnEnterHeaderFilter,
                headerFilterLiveFilter: false,
                headerFilterFunc: remoteHeaderFilter,
            },
            ...serviceTaskAuditTailColumns,
        ],
        [statusFilterOptions, serviceTaskAuditTailColumns, remoteHeaderFilter, state.module_settings]
    );

    const itemRenderer = (item, options) => (
        <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
            <span className={`${item.icon} text-primary`} />
            <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
            {item.shortcut && <span className="ml-auto border-1 surface-border border-round surface-100 text-xs p-1">{item.shortcut}</span>}
        </a>
      );
    
      const [expandedKeys, setExpandedKeys] = useState({});
      const closeMenu = useCallback(() => {
        setTimeout(() => {
          OverflowMenuProvider.setVisible?.(null);
          setExpandedKeys({});
        }, 0);
      }, []);

      const hasApplicableStandardMask = useMemo(
        () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, mergeTaskRecordDetailColumnVisibilityFromSaved),
        [state.standard_mask_filters]
      );

      const openMaskDialog = useCallback(
        createOpenMaskDialog({
          columnsVisible,
          setColumnsVisible,
          defaultColumnVisibility: DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY,
          mergeVisibilityFromSaved: mergeTaskRecordDetailColumnVisibilityFromSaved,
          setMaskVisible,
          closeMenu,
        }),
        [columnsVisible, closeMenu]
      );

      const saveUserMaskColumns = useCallback(
        createSaveUserMaskColumns({
          maskPage: MASK_PAGE,
          getTable,
          setUserMaskFilter,
          defaultColumnVisibility: DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY,
          columnsVisible,
          setColumnsVisible,
          setMaskVisible,
          toast,
        }),
        [columnsVisible, getTable, setUserMaskFilter, toast]
      );

      const confirmSaveStandardMask = useCallback(
        createConfirmSaveStandardMask({
          maskPage: MASK_PAGE,
          getTable,
          setStandardMaskFilter,
          defaultColumnVisibility: DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY,
          toast,
        }),
        [getTable, setStandardMaskFilter, toast]
      );

      const confirmApplyStandardLayoutToUserMask = useCallback(
        createConfirmApplyStandardLayoutToUserMask({
          maskPage: MASK_PAGE,
          state,
          setUserMaskFilter,
          defaultColumnVisibility: DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY,
          mergeVisibilityFromSaved: mergeTaskRecordDetailColumnVisibilityFromSaved,
          toast,
          closeMenu,
          onAppliedToTable: ({ colsForSave, attributes }) => {
            const table = getTable();
            if (!table) return;
            isRestoringRef.current = true;
            applyServiceTaskColumnLayout(table, attributes, TASK_RECORD_DETAIL_COLUMN_FIELD_ORDER);
            applyTaskRecordDetailDataColumnVisibility(table, colsForSave);
            setColumnsVisible(colsForSave);
            setTimeout(() => {
              isRestoringRef.current = false;
              captureHScroll();
              refreshFromStart();
            }, 0);
          },
        }),
        [state, closeMenu, getTable, setUserMaskFilter, toast]
      );

      const setMask = useCallback(
        createMaskSetFieldHandler({
          defaultColumnVisibility: DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY,
          columnsVisible,
          setColumnsVisible,
          getTable,
          applyVisibilityToTable: applyTaskRecordDetailDataColumnVisibility,
          toast,
        }),
        [columnsVisible, getTable, toast]
      );

      const maskSelectAll = useCallback(
        createMaskSelectAllHandler({
          defaultColumnVisibility: DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY,
          setColumnsVisible,
          getTable,
          applyVisibilityToTable: applyTaskRecordDetailDataColumnVisibility,
        }),
        [getTable]
      );

      const maskDeselectAll = useCallback(
        createMaskDeselectAllHandler({
          defaultColumnVisibility: DEFAULT_TASK_RECORD_DETAIL_COLUMN_VISIBILITY,
          setColumnsVisible,
          getTable,
          applyVisibilityToTable: applyTaskRecordDetailDataColumnVisibility,
        }),
        [getTable]
      );
    
      const handleClickOutside = (event) => {
        if (event.target.closest('[data-overflow-trigger]')) return;
        if (overlayRef.current && !overlayRef.current.contains(event.target)) {
            setExpandedKeys({});
            OverflowMenuProvider.setVisible?.(null);
        }
      };
    
      useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

      const items = [
      {
        key: '0',
        label: t('Common.Actions'),
        icon: 'pi pi-sliders-v',
        template: itemRenderer,
        items: buildMaintenanceMaskAkcieSubItems({
          maskPage: MASK_PAGE,
          getTable,
          setUserMaskFilter,
          cleanSorters,
          captureHScroll,
          refreshFromStart,
          closeMenu,
          openMaskDialog,
          openChosenNotificationPreferences,
          hasApplicableStandardMask,
          confirmApplyStandardLayoutToUserMask,
          itemRenderer,
        }),
      }
    ];

    return ( 
        <>
            <div>
            <div className='w-full h-10 flex flex-row bg-[#333]'>
            <IntersectionObserverWrapper>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="edit"><button className="disabled:opacity-25" disabled={editIcon} onClick={() => {getRecordFiles(selectedRecord?.id);setVisibleRecord(true);}}><EditIcon/></button></div>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="delete"><button className="disabled:opacity-25" onClick={confirm} disabled={deleteIcon}><DeleteIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center ' data-targetid="print"><button onClick={() => ref.current.print(false, true)}><PrintIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="first"><button className="disabled:opacity-25" onClick={() => scrollToFirst()} disabled={scrollIcons}><BackToFirstIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="back"><button className="disabled:opacity-25" onClick={() => scrollUp()} disabled={scrollIcons}><BackIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="forward"><button className="disabled:opacity-25" onClick={() => scrollDown()} disabled={scrollIcons}><ForwardIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="last"><button className="disabled:opacity-25" onClick={() => scrollToLast()} disabled={scrollIcons}><ForwardToLastIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="favorite"><button className="disabled:opacity-25" disabled onClick={() => openFavoriteDialog("PORUCHY STROJA","MaintenanceTaskRecordDetail")}><FavoriteIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="price"><button className="disabled:opacity-25" disabled onClick={() => setFastView("MaintenanceTaskRecordDetail",columnsVisible,columns)}><PriceIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center' data-targetid="vert"><VerticalLineIcon/></div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button type="button" title={t('Common.SaveStandardMask')} aria-label={t('Common.SaveStandardMask')} onClick={confirmSaveStandardMask} className="flex items-center justify-center">
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem"/>
          </div>
          </IntersectionObserverWrapper>
        </div>
        <Splitter className='h-[calc(100vh_-_177px)]' layout="vertical" pt={{ gutter: { className: 'bg-[#333] h-8' },gutterHandler: { className: 'bg-secondary ' } }}>
    <SplitterPanel className="flex align-items-center justify-content-center overflow-y-auto " minSize={20} size={25}>
            <div className="w-full relative flex flex-col overflow-x">
              <div className='xl:grid grid-cols-2 gap-1 mb-2'>
              <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                    <h1 className="text-3xl font-semibold text-left text-zinc-900">
                       PORUCHY STROJA
                    </h1>
                    <label className="block text-md font-semibold text-gray-800">Por.č.: {formMachine?.id}</label>
                    <div className="mt-6">
                        <div className="md:grid grid-cols-2 gap-2 mb-2">
                            <div>
                                <label
                                    htmlFor="name"
                                    className="block text-md font-semibold text-gray-800"
                                >{t('Common.MachineName')}</label>
                                <input
                                    name='name'
                                    type="text"
                                    readOnly value={formMachine?.name}
                                    className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="machine_type_id"
                                    className="block text-md font-semibold text-gray-800"
                                >{t('MachineList.Column.MachineTypeName')}</label>
                                <select
                                    name='machine_type_id' disabled
                                    value={formMachine?.machine_type_id}
                                    className='block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'>
                                    <option value="">{t('MachineAdd.SelectMachineType')}</option>
                                    {state.machine_types.map((machine_type) => (
                                    <option key={machine_type.id} value={machine_type.id}>{machine_type.name}</option>
                                ))}
                                </select>
                            </div>
                            <div>
                                <label
                                    htmlFor="type"
                                    className="block text-md font-semibold text-gray-800"
                                >
                                    Typ stroja
                                </label>
                                <input
                                    name='type' readOnly
                                    type="text"
                                    value={formMachine?.type}
                                    className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="serial_number"
                                    className="block text-md font-semibold text-gray-800"
                                >{t('Common.SerialNumber')}</label>
                                <input
                                    name='serial_number' readOnly
                                    type="text"
                                    value={formMachine?.serial_number}
                                    className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                />
                            </div>
                        </div>
                    </div>  
                </div>
                <div className="w-auto p-6 m-4 bg-gray-100 rounded-md shadow-md">
                    <div className="md:grid grid-cols-2 gap-2 mb-2">
                        
                        <div>
                            <label
                                htmlFor="fabrication_year"
                                className="block text-md font-semibold text-gray-800"
                            >{t('Common.FabricationYear')}</label>
                            <input
                                name='fabrication_year' readOnly
                                type="number"
                                value={formMachine?.fabrication_year}
                                className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="last_overhaul"
                                className="block text-md font-semibold text-gray-800"
                            >{t('Common.LastOverhaul')}</label>
                            <input 
                                name='last_overhaul' readOnly
                                type="date"
                                value={formMachine?.last_overhaul}
                                className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="description"
                                className="block text-md font-semibold text-gray-800"
                            >{t('Common.MachineDescription')}</label>
                            <textarea
                                name='description' readOnly
                                value={formMachine?.description}
                                className="block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                            />
                        </div> 
                        <div>
                        <label
                            htmlFor="manufacturer"
                            className="block text-md font-semibold text-gray-800 mt-2"
                        >{t('Common.Manufacturer')}</label>
                        <input
                            name='manufacturer' readOnly
                            type="text"
                            value={formMachine?.manufacturer}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                    </div>
                    </div>
                </div>
                </div> 
                </div>
                 </SplitterPanel>
    <SplitterPanel className="flex flex-col align-items-center justify-content-center w-full overflow-y-auto" size={40} style={{minHeight: '110px'}}>
    <ReactTabulator
      key={formMachine?.id || "no-machine"}   // odporúčam (Tabulator občas “drží” staré data)
      onRef={(r) => (ref.current = r.current)}
      className='h-full w-full' //176px h-[calc(100vh_-_176px)]
      columns={columns}
      events={{
        tableBuilt: () => {
            const table = getTable();
            if (!table) return;
            isRestoringRef.current = true;
            const effective = getEffectiveMask(MASK_PAGE, state);
            if (effective?.type === 'user' && effective.userMask) {
                restoreServiceTaskUserMaskToTable(table, effective.userMask, {
                    canonicalFieldOrder: TASK_RECORD_DETAIL_COLUMN_FIELD_ORDER,
                    setColumnsVisible,
                    clearHeaderFiltersWhenEmpty: true,
                    applyColumnVisibilityFn: (tbl, jc, setVis) => {
                        const merged = mergeTaskRecordDetailColumnVisibilityFromSaved(jc);
                        setVis(merged);
                        applyTaskRecordDetailDataColumnVisibility(tbl, merged);
                    },
                });
            } else if (effective?.type === 'standard' && effective.standardMask) {
                const m = effective.standardMask;
                const jsonColumns = safeParse(m.columns, {});
                applyServiceTaskColumnLayout(table, safeParse(m.attributes, []), TASK_RECORD_DETAIL_COLUMN_FIELD_ORDER);
                if (jsonColumns && typeof jsonColumns === 'object') {
                    const merged = mergeTaskRecordDetailColumnVisibilityFromSaved(jsonColumns);
                    setColumnsVisible(merged);
                    applyTaskRecordDetailDataColumnVisibility(table, merged);
                }
            } else {
                applyTaskRecordDetailDataColumnVisibility(table, columnsVisible);
            }
            setTimeout(() => {
                isRestoringRef.current = false;
            }, 0);
            tableBuiltRef.current = true;
            refreshFromStart();
        },
        ...getTabulatorPersistLayoutEvents(MASK_PAGE, ref, setUserMaskFilter),
        headerFilterChanged: () => {
            if (!isRestoringRef.current) {
                captureHScroll();
                refreshFromStart();
            }
        },
        sortChanged: () => {
            if (!isRestoringRef.current) {
                captureHScroll();
                refreshFromStart();
            }
        },
        renderComplete: () => restoreHScroll(),
      }}
        options={{
        layout: "fitColumns",
        rowHeight: 42,
        dataLoader: true,
        dataLoaderLoading: t('Common.LoadingData'),
        dataLoaderError: t('Common.LoadFailed'),
        placeholder: t('Common.NoData'),
        progressiveLoad: "scroll",
        progressiveLoadScrollMargin: 80,
        progressiveLoadDelay: 200,
        pagination: false,
        paginationSize: pageSizePc,
        filterMode: "remote",
        sortMode: "remote",
        headerSortTristate: true,
        ajaxURL: "/api/service_tasks/paged",
        ajaxParams: () => ({}),
        ajaxRequestFunc: createDedupedAjaxRequest(buildRequestParams, tabulatorGet, inFlightRef, completedCacheRef),
        ajaxRequesting: () => {
            if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
            getTable()?.alert?.(t('Common.LoadingData'));
            return true;
        },
        ajaxResponse: (url, params, response) => { getTable()?.clearAlert?.(); return response; },
        ajaxError: () => { getTable()?.clearAlert?.(); },
        renderHorizontal:"virtual",
        movableColumns: true,
        printAsHtml:true,
        printRowRange:"active",
        printCopyStyle:true,
        printHeader: function () {
            const bodyHtml = ReactDOMServer.renderToStaticMarkup(
                <div className="w-full text-[11px]">
{/* 2. riadok – polia v dvoch stĺpcoch */}
                    <div className="grid grid-cols-2 gap-4 mb-2">
                        {/* Ľavý stĺpec */}
                        <div className="space-y-2">
                            <div>
                                <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Ui.R67.Por_c')}</label>
                                <input
                                    type="text"
                                    readOnly
                                    value={formMachine?.id ?? ''}
                                    className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                />
                            </div>
    
                            <div>
                                <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.MachineName')}</label>
                                <input
                                    name="name"
                                    type="text"
                                    readOnly
                                    value={formMachine?.name || ''}
                                    className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[11px] text-left font-semibold text-gray-800">{t('MachineList.Column.MachineTypeName')}</label>
                                    <input
                                        name="machine_type_name"
                                        type="text"
                                        readOnly
                                        value={formMachine?.machine_type?.name || ''}
                                        className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] text-left font-semibold text-gray-800">
                                        Typ stroja
                                    </label>
                                    <input
                                        name="type"
                                        type="text"
                                        readOnly
                                        value={formMachine?.type || ''}
                                        className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
                        </div>
    
                        {/* Pravý stĺpec */}
                        <div className="space-y-2">
                            <div>
                                <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.SerialNumber')}</label>
                                <input
                                    name="serial_number"
                                    type="text"
                                    readOnly
                                    value={formMachine?.serial_number || ''}
                                    className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                />
                            </div>
    
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.FabricationYear')}</label>
                                    <input
                                        name="fabrication_year"
                                        type="number"
                                        readOnly
                                        value={formMachine?.fabrication_year || ''}
                                        className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.LastOverhaul')}</label>
                                    <input
                                        name="last_overhaul"
                                        type="date"
                                        readOnly
                                        value={formMachine?.last_overhaul || ''}
                                        className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                    />
                                </div>
                            </div>
    
                            <div>
                                <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.Manufacturer')}</label>
                                <input
                                    name="manufacturer"
                                    type="text"
                                    readOnly
                                    value={formMachine?.manufacturer || ''}
                                    className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>
                    </div>
    
                    {/* 3. riadok – popis cez celú šírku */}
                    <div className="mt-1">
                        <label className="block text-[11px] text-left font-semibold text-gray-800">{t('Common.MachineDescription')}</label>
                        <textarea
                            name="description"
                            readOnly
                            value={formMachine?.description || ''}
                            className="mt-1 block w-full px-2 text-[11px] bg-white border border-gray-300 rounded-md min-h-[40px]"
                        />
                    </div>
                </div>
            );
            return buildCanonicalPrintHeaderHtml({
                branding: state?.branding,
                productFallbackSrc: defaultProductLogoSrc,
                companySettingsPrint: state?.company_settings_print,
                moduleSettings: state?.module_settings,
                title: t('Print.Title.MaintenanceTaskRecordDetail'),
                printDateLabel: t('Print.DateLabel'),
                bodyHtml,
            });
        },
                printFooter: function () {
                    return buildCanonicalPrintFooterHtml({ companySettingsPrint: state?.company_settings_print });
                }
      }}/>
    </SplitterPanel>
    </Splitter>
<Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
            <div className="flex flex-col">
              <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
              <div className="flex flex-wrap gap-2 mb-2">
                <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
                <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
              </div>
            {columns.map((item) => {
                if (!item.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
                        return <div 
                            key={item.field} 
                            className="flex flex-row border border-black rounded-md p-2 m-1"
                        > 
                                <MaintenanceMaskDialogCheckbox field={item.field} inputId={`mtrd-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} /><label htmlFor={`mtrd-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title || item.field}</label>
                        </div> 
                    })}
                        <button type="button" onClick={saveUserMaskColumns} className='m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700'>{t('Common.SaveUpper')}</button>
            </div>
        </Dialog>
            </div>
            </> 
            );
} 
